import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ticketsAPI } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { ESTADOS, LUGARES } from '../../lib/constants';
import { Search, Download, FileText, RefreshCw, Calendar, ChevronLeft, ChevronRight, Package,
         Printer, MapPin, Clock, Navigation, Loader2, Bot, X, Phone } from 'lucide-react';

const API = import.meta.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND_URL;

const ESTADO_STYLES = {
  Notificada:  'bg-blue-100 text-blue-800 border-blue-200',
  Asignada:    'bg-amber-100 text-amber-800 border-amber-200',
  Realizada:   'bg-emerald-100 text-emerald-800 border-emerald-200',
  Cancelada:   'bg-red-100 text-red-800 border-red-200',
  Ausente:     'bg-slate-100 text-slate-700 border-slate-200',
  Pendiente:   'bg-orange-100 text-orange-800 border-orange-200',
  'En espera': 'bg-purple-100 text-purple-800 border-purple-200',
};

const PAGE_SIZE = 25;

const AdminTickets = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [diaStats, setDiaStats] = useState(null);
  const [assigningRuta, setAssigningRuta] = useState(null); // ticket_id being assigned

  const today = new Date().toISOString().split('T')[0];
  const [filters, setFilters] = useState({
    search: '',
    estado: '',
    lugar: '',
    fechaExacta: today,   // por defecto hoy
    fechaDesde: '',
    fechaHasta: '',
    modoFecha: 'dia',     // 'dia' | 'rango' | 'todos'
  });

  const getDateParams = () => {
    if (filters.modoFecha === 'dia' && filters.fechaExacta) {
      return { fecha_desde: filters.fechaExacta, fecha_hasta: filters.fechaExacta };
    }
    if (filters.modoFecha === 'rango') {
      return { fecha_desde: filters.fechaDesde || undefined, fecha_hasta: filters.fechaHasta || undefined };
    }
    return {};
  };

  const loadTickets = useCallback(async (pageNum = 0) => {
    setLoading(true);
    try {
      const params = {
        skip: pageNum * PAGE_SIZE,
        limit: PAGE_SIZE,
        ...getDateParams(),
      };
      if (filters.estado) params.estado = filters.estado;
      if (filters.lugar) params.lugar = filters.lugar;
      if (filters.search) params.search = filters.search;

      const response = await ticketsAPI.list(params);
      setTickets(response.data.tickets || []);
      setTotal(response.data.total || 0);
      setPage(pageNum);
    } catch (err) {
      console.error('Error loading tickets:', err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // Load dia stats when in día exacto mode
  const loadDiaStats = useCallback(async () => {
    if (filters.modoFecha !== 'dia' || !filters.fechaExacta) {
      setDiaStats(null);
      return;
    }
    try {
      const params = new URLSearchParams({ fecha: filters.fechaExacta });
      if (filters.estado) params.set('estado', filters.estado);
      if (filters.lugar) params.set('lugar', filters.lugar);
      const res = await fetch(`${API}/api/tickets/dia-stats?${params}`);
      const data = await res.json();
      setDiaStats(data);
    } catch (_) {}
  }, [filters]);

  useEffect(() => {
    loadTickets(0);
    loadDiaStats();
  }, [filters]);

  // ── Capacidad diaria por zona ──────────────────────────────────────────
  const [capacidad, setCapacidad] = useState(null);
  useEffect(() => {
    if (filters.modoFecha !== 'dia' || !filters.fechaExacta) { setCapacidad(null); return; }
    const storedAuth = JSON.parse(localStorage.getItem('auth-storage') || '{}');
    const token = storedAuth?.state?.token;
    fetch(`${API}/api/capacidad-diaria?fecha=${filters.fechaExacta}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    })
      .then(r => r.json())
      .then(d => setCapacidad(d))
      .catch(() => setCapacidad(null));
  }, [filters.fechaExacta, filters.modoFecha]);

  const exportPDF = () => {
    if (filters.modoFecha === 'dia' && filters.fechaExacta) {
      window.open(`${API}/api/resumen-diario/export/pdf?fecha=${filters.fechaExacta}`, '_blank');
    } else if (filters.modoFecha === 'rango' && filters.fechaDesde) {
      window.open(`${API}/api/resumen-diario/export/pdf?fecha=${filters.fechaDesde}`, '_blank');
    }
  };

  const [pdfZonaLoading, setPdfZonaLoading] = useState({ casco: false, puerta: false });

  const exportPDFZona = async (zona) => {
    const fechaParam = filters.modoFecha === 'dia' && filters.fechaExacta
      ? filters.fechaExacta
      : filters.modoFecha === 'rango' && filters.fechaDesde
        ? filters.fechaDesde
        : new Date().toISOString().slice(0, 10);

    const key = zona.includes('Casco') ? 'casco' : 'puerta';
    setPdfZonaLoading(prev => ({ ...prev, [key]: true }));
    try {
      const storedAuth = JSON.parse(localStorage.getItem('auth-storage') || '{}');
      const token = storedAuth?.state?.token;
      const url = `${API}/api/export/pdf/zona-dia?fecha=${fechaParam}&zona=${encodeURIComponent(zona)}`;
      const res = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: 'Error desconocido' }));
        alert(`Error: ${err.detail || 'Sin tickets para esta zona/fecha'}`);
        return;
      }
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `ruta_${key}_${fechaParam}.pdf`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      alert('Error al generar PDF: ' + e.message);
    } finally {
      setPdfZonaLoading(prev => ({ ...prev, [key]: false }));
    }
  };

  const exportExcel = () => {
    const params = new URLSearchParams();
    const dateParams = getDateParams();
    if (dateParams.fecha_desde) params.set('fecha_desde', dateParams.fecha_desde);
    if (dateParams.fecha_hasta) params.set('fecha_hasta', dateParams.fecha_hasta);
    if (filters.estado) params.set('estado', filters.estado);
    if (filters.lugar) params.set('lugar', filters.lugar);
    window.open(`${API}/api/export/tickets?${params.toString()}`, '_blank');
  };

  const setDia = (offset) => {
    const d = new Date(filters.fechaExacta || today);
    d.setDate(d.getDate() + offset);
    setFilters(f => ({ ...f, fechaExacta: d.toISOString().split('T')[0], modoFecha: 'dia' }));
  };

  const [routeLoading, setRouteLoading] = useState(false);
  const [routeResult, setRouteResult] = useState(null);
  const [showRouteSheet, setShowRouteSheet] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  const TICKET_LOAD_LIMIT = 500;

  const generateRouteSheet = async () => {
    setRouteLoading(true);
    try {
      // Load ALL tickets matching current filters
      const params = { skip: 0, limit: TICKET_LOAD_LIMIT, ...getDateParams() };
      if (filters.estado) params.estado = filters.estado;
      if (filters.lugar) params.lugar = filters.lugar;
      if (filters.search) params.search = filters.search;

      const response = await ticketsAPI.list(params);
      const allTickets = (response.data.tickets || []).filter(t => t.estado !== 'Cancelada' && t.estado !== 'Realizada');

      if (allTickets.length < 2) {
        alert(`Se necesitan al menos 2 tickets activos para generar la hoja de ruta. Encontrados: ${allTickets.length}`);
        return;
      }

      if (allTickets.length > 150) {
        const ok = window.confirm(`Se van a optimizar ${allTickets.length} tickets. El proceso puede tardar 1-2 minutos. ¿Continuar?`);
        if (!ok) { setRouteLoading(false); return; }
      }

      const storedAuth = JSON.parse(localStorage.getItem('auth-storage') || '{}');
      const token = storedAuth?.state?.token;

      const res = await fetch(`${API}/api/rutas/optimize-tickets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ ticket_ids: allTickets.map(t => t.ticket_id), jornada_horas: 8, tiempo_parada_min: 15 })
      });
      if (!res.ok) throw new Error(`Error ${res.status}: ${await res.text()}`);
      const data = await res.json();
      setRouteResult({ ...data, fecha: filters.fechaExacta || filters.fechaDesde || today });
      setShowRouteSheet(true);
    } catch (e) {
      alert('Error al generar hoja de ruta: ' + e.message);
    } finally {
      setRouteLoading(false);
    }
  };

  const [bulkAssigning, setBulkAssigning] = useState(false);

  // ── Reasignación automática de zonas ──────────────────────────────────────
  const [showRezonaModal, setShowRezonaModal] = useState(false);
  const [rezonaTab, setRezonaTab] = useState('autozona'); // 'autozona' | 'geocodificar'
  const [rezonaJob, setRezonaJob] = useState(null);
  const [rezonaLoading, setRezonaLoading] = useState(false);
  const [rezonaSoloSinZona, setRezonaSoloSinZona] = useState(true);

  // Geocoding job state
  const [geocodeJob, setGeocodeJob] = useState(null);
  const [geocodeLoading, setGeocodeLoading] = useState(false);
  const [geocodeVelocidad, setGeocodeVelocidad] = useState(1000); // ms entre requests

  const VELOCIDADES = [
    { label: 'Lenta', ms: 2000, rpm: 30, etaH: 17, desc: '30/min · ~17h' },
    { label: 'Normal', ms: 1000, rpm: 60, etaH: 8.5, desc: '60/min · ~8.5h' },
    { label: 'Rápida', ms: 500, rpm: 120, etaH: 4.3, desc: '120/min · ~4.3h' },
  ];

  const startReasignarZonas = async (dryRun = false) => {
    setRezonaLoading(true);
    try {
      const storedAuth = JSON.parse(localStorage.getItem('auth-storage') || '{}');
      const token = storedAuth?.state?.token;
      const params = new URLSearchParams({ solo_sin_zona: rezonaSoloSinZona, dry_run: dryRun });
      const res = await fetch(`${API}/api/admin/reasignar-zonas?${params}`, {
        method: 'POST',
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      const data = await res.json();
      setRezonaJob({ job_id: data.job_id, status: 'running', percent: 0, message: 'Iniciando...' });
    } catch (e) {
      alert('Error al iniciar: ' + e.message);
    } finally {
      setRezonaLoading(false);
    }
  };

  const startGeocoding = async () => {
    setGeocodeLoading(true);
    try {
      const storedAuth = JSON.parse(localStorage.getItem('auth-storage') || '{}');
      const token = storedAuth?.state?.token;
      const params = new URLSearchParams({ delay_ms: geocodeVelocidad, limite: 0 });
      const res = await fetch(`${API}/api/admin/geocodificar-cadiz?${params}`, {
        method: 'POST',
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      const data = await res.json();
      setGeocodeJob({ job_id: data.job_id, status: 'running', percent: 0, message: 'Iniciando...' });
    } catch (e) {
      alert('Error al iniciar geocodificación: ' + e.message);
    } finally {
      setGeocodeLoading(false);
    }
  };

  const stopGeocoding = async () => {
    if (!geocodeJob?.job_id) return;
    try {
      const storedAuth = JSON.parse(localStorage.getItem('auth-storage') || '{}');
      const token = storedAuth?.state?.token;
      await fetch(`${API}/api/admin/geocodificar-cadiz/stop/${geocodeJob.job_id}`, {
        method: 'POST',
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
    } catch (e) { console.error(e); }
  };

  // Poll reasignar job
  useEffect(() => {
    if (!rezonaJob?.job_id || rezonaJob.status === 'done' || rezonaJob.status === 'error') return;
    const iv = setInterval(async () => {
      try {
        const storedAuth = JSON.parse(localStorage.getItem('auth-storage') || '{}');
        const token = storedAuth?.state?.token;
        const res = await fetch(`${API}/api/admin/reasignar-zonas/status/${rezonaJob.job_id}`, {
          headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        });
        const data = await res.json();
        setRezonaJob(prev => ({ ...prev, ...data }));
        if (data.status === 'done') {
          clearInterval(iv);
          if (!data.dry_run) loadTickets(0);
        }
      } catch {}
    }, 1500);
    return () => clearInterval(iv);
  }, [rezonaJob?.job_id, rezonaJob?.status]);

  // Poll geocoding job
  useEffect(() => {
    if (!geocodeJob?.job_id || ['done', 'error', 'stopped'].includes(geocodeJob.status)) return;
    const iv = setInterval(async () => {
      try {
        const storedAuth = JSON.parse(localStorage.getItem('auth-storage') || '{}');
        const token = storedAuth?.state?.token;
        const res = await fetch(`${API}/api/admin/geocodificar-cadiz/status/${geocodeJob.job_id}`, {
          headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        });
        const data = await res.json();
        setGeocodeJob(prev => ({ ...prev, ...data }));
        if (['done', 'error', 'stopped'].includes(data.status)) {
          clearInterval(iv);
          loadTickets(0);
        }
      } catch {}
    }, 2000);
    return () => clearInterval(iv);
  }, [geocodeJob?.job_id, geocodeJob?.status]);

  const bulkAssignRuta = async (lugar) => {
    if (!window.confirm(`¿Asignar "${lugar}" a TODOS los tickets que coincidan con el filtro actual? Esta acción sobreescribirá la zona existente.`)) return;
    setBulkAssigning(true);
    try {
      const storedAuth = JSON.parse(localStorage.getItem('auth-storage') || '{}');
      const token = storedAuth?.state?.token;
      const dateParams = getDateParams();
      const body = {
        lugar,
        ...(dateParams.fecha_desde && { fecha_desde: dateParams.fecha_desde }),
        ...(dateParams.fecha_hasta && { fecha_hasta: dateParams.fecha_hasta }),
        ...(filters.estado && { estado: filters.estado }),
      };
      const res = await fetch(`${API}/api/tickets/bulk-assign-ruta`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify(body)
      });
      const data = await res.json();
      if (data.success) {
        alert(`✓ ${data.modified} tickets asignados a "${lugar}"`);
        loadTickets(0); // Reload
      }
    } catch (e) {
      alert('Error: ' + e.message);
    } finally {
      setBulkAssigning(false);
    }
  };

  const assignRuta = async (ticketId, lugar) => {
    try {
      const storedAuth = JSON.parse(localStorage.getItem('auth-storage') || '{}');
      const token = storedAuth?.state?.token;
      await fetch(`${API}/api/tickets/${ticketId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ lugar })
      });
      // Update local state immediately (no reload)
      setTickets(prev => prev.map(t => t.ticket_id === ticketId ? { ...t, lugar } : t));
    } catch (e) {
      console.error('Error assigning ruta:', e);
    } finally {
      setAssigningRuta(null);
    }
  };

  const downloadRoutePDF = async () => {
    if (!routeResult) return;
    setPdfLoading(true);
    try {
      const storedAuth = JSON.parse(localStorage.getItem('auth-storage') || '{}');
      const token = storedAuth?.state?.token;
      const res = await fetch(`${API}/api/rutas/route-sheet-pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({
          fecha: routeResult.fecha,
          tickets: routeResult.tickets_ordenados || [],
          optimization: routeResult.optimization,
          total_tickets: routeResult.total_tickets,
        })
      });
      if (!res.ok) throw new Error(`Error ${res.status}`);
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `hoja_ruta_${routeResult.fecha || 'ruta'}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert('Error al generar PDF: ' + e.message);
    } finally {
      setPdfLoading(false);
    }
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const canExportPDF = filters.modoFecha === 'dia' && filters.fechaExacta;

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const displayDate = filters.fechaExacta
    ? new Date(filters.fechaExacta).toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
    : '';

  return (
    <div data-testid="admin-tickets-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <p className="text-xs uppercase tracking-widest text-slate-400 font-bold mb-0.5">Gestión</p>
          <h1 className="text-2xl font-bold text-slate-900">Bandeja de Tickets</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            {loading ? 'Cargando...' : `${total.toLocaleString('es-ES')} tickets encontrados`}
          </p>
          {/* Capacidad diaria badge */}
          {capacidad && (
            <div className="flex items-center gap-2 mt-1.5 flex-wrap" data-testid="capacidad-diaria">
              {[
                { key: 'casco_antiguo', label: 'Casco Antiguo', color: 'emerald' },
                { key: 'puerta_tierra', label: 'Puerta Tierra', color: 'blue' },
              ].map(({ key, label, color }) => {
                const z = capacidad[key];
                if (!z) return null;
                const pct = Math.round(z.count / capacidad.maximo_por_zona * 100);
                const bg = z.completa ? 'bg-red-50 border-red-300' : pct >= 75 ? `bg-amber-50 border-amber-300` : `bg-${color}-50 border-${color}-200`;
                const txt = z.completa ? 'text-red-700' : pct >= 75 ? 'text-amber-700' : `text-${color}-700`;
                return (
                  <span key={key} className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full border ${bg} ${txt}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${z.completa ? 'bg-red-500' : pct >= 75 ? 'bg-amber-500' : `bg-${color}-500`}`} />
                    {label}: {z.count}/{capacidad.maximo_por_zona}
                    {z.completa && ' — COMPLETA'}
                  </span>
                );
              })}
            </div>
          )}
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={() => loadTickets(0)} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4 mr-1.5" /> Actualizar
          </Button>
          <Button onClick={exportExcel} variant="outline" size="sm" data-testid="export-excel-btn">
            <Download className="w-4 h-4 mr-1.5" /> Excel
          </Button>
          {canExportPDF && (
            <Button onClick={exportPDF} className="bg-red-600 hover:bg-red-700 text-white" size="sm" data-testid="export-pdf-btn">
              <FileText className="w-4 h-4 mr-1.5" /> PDF del día
            </Button>
          )}
          {/* PDFs por zona con IA */}
          {canExportPDF && (
            <div className="flex items-center gap-1 border border-emerald-200 rounded-lg px-2 py-1 bg-emerald-50">
              <FileText className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" />
              <span className="text-xs text-emerald-700 font-medium mr-1">PDF IA:</span>
              <button
                onClick={() => exportPDFZona('Casco Antiguo')}
                disabled={pdfZonaLoading.casco}
                className="text-xs px-2 py-0.5 bg-emerald-700 text-white rounded-md hover:bg-emerald-800 disabled:opacity-50 transition-colors flex items-center gap-1"
                data-testid="btn-pdf-casco"
                title="PDF Hoja de Ruta Casco Antiguo con paradas en orden IA"
              >
                {pdfZonaLoading.casco ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                {pdfZonaLoading.casco ? 'Generando...' : 'Casco Antiguo'}
              </button>
              <button
                onClick={() => exportPDFZona('Puerta Tierra')}
                disabled={pdfZonaLoading.puerta}
                className="text-xs px-2 py-0.5 bg-blue-700 text-white rounded-md hover:bg-blue-800 disabled:opacity-50 transition-colors flex items-center gap-1"
                data-testid="btn-pdf-puerta"
                title="PDF Hoja de Ruta Puerta Tierra con paradas en orden IA"
              >
                {pdfZonaLoading.puerta ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                {pdfZonaLoading.puerta ? 'Generando...' : 'Puerta Tierra'}
              </button>
            </div>
          )}
          {/* Asignación masiva de ruta */}
          <div className="flex items-center gap-1 border border-slate-200 rounded-lg px-2 py-1 bg-slate-50">
            <MapPin className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
            <span className="text-xs text-slate-500 mr-1">Ruta masiva:</span>
            <button
              onClick={() => bulkAssignRuta('Casco Antiguo')}
              disabled={bulkAssigning || total === 0}
              className="text-xs px-2 py-0.5 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 disabled:opacity-50 transition-colors"
              data-testid="bulk-assign-casco"
            >
              {bulkAssigning ? '...' : 'Casco Antiguo'}
            </button>
            <button
              onClick={() => bulkAssignRuta('Puerta Tierra')}
              disabled={bulkAssigning || total === 0}
              className="text-xs px-2 py-0.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors"
              data-testid="bulk-assign-puerta"
            >
              {bulkAssigning ? '...' : 'Puerta Tierra'}
            </button>
          </div>
          <Button
            onClick={generateRouteSheet}
            disabled={routeLoading || loading || total === 0}
            className="bg-slate-800 hover:bg-slate-900 text-white disabled:opacity-50"
            size="sm"
            data-testid="btn-hoja-ruta-ia"
          >
            {routeLoading ? (
              <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Generando...</>
            ) : (
              <><Bot className="w-4 h-4 mr-1.5" /> Hoja de Ruta IA</>
            )}
          </Button>
          <Button
            onClick={() => { setShowRezonaModal(true); setRezonaJob(null); }}
            variant="outline"
            size="sm"
            data-testid="btn-reasignar-zonas"
            className="border-teal-300 text-teal-700 hover:bg-teal-50"
          >
            <MapPin className="w-4 h-4 mr-1.5" /> Auto-Zonas
          </Button>
        </div>
      </div>

      {/* Día Stats Banner */}
      {filters.modoFecha === 'dia' && diaStats && (
        <div className="flex items-center gap-6 bg-teal-50 border border-teal-200 rounded-xl px-5 py-3 mb-4" data-testid="dia-stats-banner">
          <Calendar className="w-5 h-5 text-teal-600 flex-shrink-0" />
          <div className="flex items-center gap-1.5">
            <span className="text-2xl font-bold text-teal-800">{diaStats.total_tickets.toLocaleString('es-ES')}</span>
            <span className="text-sm text-teal-600 font-medium">tickets</span>
          </div>
          <div className="w-px h-6 bg-teal-300" />
          <div className="flex items-center gap-1.5">
            <Package className="w-4 h-4 text-teal-500" />
            <span className="text-2xl font-bold text-teal-800">{diaStats.total_enseres.toLocaleString('es-ES')}</span>
            <span className="text-sm text-teal-600 font-medium">enseres</span>
          </div>
          <div className="w-px h-6 bg-teal-300" />
          <span className="text-sm text-teal-600 capitalize">
            {new Date(filters.fechaExacta + 'T12:00:00').toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
          </span>
        </div>
      )}

      {/* Filtros */}
      <Card className="mb-4 border-slate-200 shadow-none">
        <CardContent className="pt-4 pb-4">
          {/* Selector de modo fecha */}
          <div className="flex gap-2 mb-4">
            {[
              { key: 'dia', label: 'Día exacto' },
              { key: 'rango', label: 'Rango de fechas' },
              { key: 'todos', label: 'Todos' },
            ].map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setFilters(f => ({ ...f, modoFecha: key }))}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  filters.modoFecha === key
                    ? 'bg-teal-600 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
                data-testid={`modo-fecha-${key}`}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Fecha por día — con navegación */}
          {filters.modoFecha === 'dia' && (
            <div className="flex items-center gap-2 mb-4 bg-teal-50 border border-teal-200 rounded-xl p-3">
              <Calendar className="w-5 h-5 text-teal-600 flex-shrink-0" />
              <button onClick={() => setDia(-1)} className="p-1 rounded hover:bg-teal-100 transition-colors" data-testid="btn-day-prev">
                <ChevronLeft className="w-5 h-5 text-teal-700" />
              </button>
              <div className="flex items-center gap-2 flex-1">
                <input
                  type="date"
                  value={filters.fechaExacta}
                  onChange={(e) => setFilters(f => ({ ...f, fechaExacta: e.target.value }))}
                  className="border border-teal-200 rounded-lg px-3 py-1.5 text-sm bg-white focus:ring-2 focus:ring-teal-300 outline-none"
                  data-testid="input-fecha-exacta"
                />
                <span className="text-sm font-medium text-teal-800 capitalize">{displayDate}</span>
              </div>
              <button onClick={() => setDia(1)} className="p-1 rounded hover:bg-teal-100 transition-colors" data-testid="btn-day-next">
                <ChevronRight className="w-5 h-5 text-teal-700" />
              </button>
              <button
                onClick={() => setFilters(f => ({ ...f, fechaExacta: today }))}
                className="text-xs px-2 py-1 bg-teal-600 text-white rounded-md hover:bg-teal-700 transition-colors"
                data-testid="btn-hoy"
              >
                Hoy
              </button>
            </div>
          )}

          {/* Rango de fechas */}
          {filters.modoFecha === 'rango' && (
            <div className="flex gap-3 items-end mb-4">
              <div>
                <Label className="text-xs text-slate-500">Fecha desde</Label>
                <input
                  type="date"
                  value={filters.fechaDesde}
                  onChange={(e) => setFilters(f => ({ ...f, fechaDesde: e.target.value }))}
                  className="border border-slate-200 rounded-lg px-3 py-2 text-sm"
                  data-testid="input-fecha-desde"
                />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Fecha hasta</Label>
                <input
                  type="date"
                  value={filters.fechaHasta}
                  onChange={(e) => setFilters(f => ({ ...f, fechaHasta: e.target.value }))}
                  className="border border-slate-200 rounded-lg px-3 py-2 text-sm"
                  data-testid="input-fecha-hasta"
                />
              </div>
            </div>
          )}

          {/* Otros filtros */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="ID, nombre, dirección..."
                value={filters.search}
                onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))}
                className="pl-9 text-sm"
                data-testid="input-search"
              />
            </div>
            <select
              value={filters.estado}
              onChange={(e) => setFilters(f => ({ ...f, estado: e.target.value }))}
              className="px-3 py-2 border border-slate-200 rounded-lg text-sm text-slate-700 bg-white"
              data-testid="select-estado"
            >
              <option value="">Todos los estados</option>
              {ESTADOS.map(e => <option key={e.value} value={e.value}>{e.label}</option>)}
            </select>
            <select
              value={filters.lugar}
              onChange={(e) => setFilters(f => ({ ...f, lugar: e.target.value }))}
              className="px-3 py-2 border border-slate-200 rounded-lg text-sm text-slate-700 bg-white"
              data-testid="select-lugar"
            >
              <option value="">Todas las rutas</option>
              {LUGARES.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
              <option value="sin_asignar">Sin ruta asignada</option>
              <option value="reprogramados">⚠ Reprogramados</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Tabla */}
      {loading ? (
        <div className="flex items-center justify-center py-16" data-testid="tickets-loading">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-teal-600 border-t-transparent mx-auto mb-2"></div>
            <p className="text-sm text-slate-500">Cargando tickets...</p>
          </div>
        </div>
      ) : tickets.length === 0 ? (
        <Card className="border-slate-200 shadow-none">
          <CardContent className="py-16 text-center">
            <Calendar className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="font-medium text-slate-600">No hay tickets para este filtro</p>
            <p className="text-sm text-slate-400 mt-1">
              {filters.modoFecha === 'dia' && filters.fechaExacta
                ? `No se encontraron recogidas para el ${formatDate(filters.fechaExacta)}`
                : 'Prueba a cambiar los filtros'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">ID</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">Ciudadano</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">Dirección</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">Ruta</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">Fecha</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">Estado</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">Items</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {tickets.map((ticket) => {
                  const ruta = ticket.lugar;
                  const isAssigning = assigningRuta === ticket.ticket_id;
                  return (
                  <tr key={ticket.ticket_id} className="hover:bg-slate-50 transition-colors" data-testid={`ticket-row-${ticket.ticket_id}`}>
                    <td className="px-4 py-3 text-sm font-mono font-semibold text-slate-800">{ticket.ticket_id}</td>
                    <td className="px-4 py-3">
                      <p className="text-sm font-medium text-slate-800 flex items-center gap-1.5">
                        {ticket.nombre_ciudadano}
                        {ticket.resumen_llamada && (
                          <Phone className="w-3.5 h-3.5 text-teal-500" title="Aviso creado por agente de voz — con resumen de llamada" />
                        )}
                      </p>
                      <p className="text-xs text-slate-400">{ticket.email || ''}</p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm text-slate-700">{ticket.direccion || '—'}</p>
                      {(ticket.bloque || ticket.piso) && (
                        <p className="text-xs text-slate-400 mt-0.5">
                          {ticket.bloque && <span className="mr-2">Bl. {ticket.bloque}</span>}
                          {ticket.piso && <span>Piso {ticket.piso}</span>}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {isAssigning ? (
                        <div className="flex flex-col gap-1">
                          <button
                            onClick={() => assignRuta(ticket.ticket_id, 'Casco Antiguo')}
                            className="text-xs px-2 py-1 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 transition-colors"
                            data-testid={`assign-casco-${ticket.ticket_id}`}
                          >Casco Antiguo</button>
                          <button
                            onClick={() => assignRuta(ticket.ticket_id, 'Puerta Tierra')}
                            className="text-xs px-2 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                            data-testid={`assign-puerta-${ticket.ticket_id}`}
                          >Puerta Tierra</button>
                          <button
                            onClick={() => setAssigningRuta(null)}
                            className="text-xs px-2 py-1 bg-slate-200 text-slate-600 rounded-md hover:bg-slate-300 transition-colors"
                          >Cancelar</button>
                        </div>
                      ) : ruta === 'Casco Antiguo' ? (
                        <button
                          onClick={() => setAssigningRuta(ticket.ticket_id)}
                          className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 border border-emerald-200 hover:bg-emerald-200 transition-colors"
                          title="Clic para cambiar ruta"
                          data-testid={`ruta-badge-${ticket.ticket_id}`}
                        >Casco Antiguo</button>
                      ) : ruta === 'Puerta Tierra' ? (
                        <button
                          onClick={() => setAssigningRuta(ticket.ticket_id)}
                          className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 border border-blue-200 hover:bg-blue-200 transition-colors"
                          title="Clic para cambiar ruta"
                          data-testid={`ruta-badge-${ticket.ticket_id}`}
                        >Puerta Tierra</button>
                      ) : (
                        <button
                          onClick={() => setAssigningRuta(ticket.ticket_id)}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-500 border border-dashed border-slate-300 hover:bg-slate-200 transition-colors"
                          data-testid={`ruta-asignar-${ticket.ticket_id}`}
                        >+ Asignar</button>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">
                      {ticket.fecha_preferida_retirada
                        ? formatDate(ticket.fecha_preferida_retirada)
                        : '—'}
                      {ticket.reprogramado && ticket.fecha_original && (
                        <div className="flex items-center gap-1 mt-0.5">
                          <span className="inline-flex items-center gap-1 text-xs bg-amber-100 text-amber-700 border border-amber-300 rounded px-1.5 py-0.5 font-medium" title={`Fecha original: ${formatDate(ticket.fecha_original)}`}>
                            ⚠ Reprogramado
                          </span>
                          <span className="text-xs text-slate-400">orig. {formatDate(ticket.fecha_original)}</span>
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${ESTADO_STYLES[ticket.estado] || 'bg-slate-100 text-slate-600 border-slate-200'}`}>
                        {ticket.estado}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-500">{ticket.num_items || (ticket.items?.length ?? '—')}</td>
                    <td className="px-4 py-3">
                      <Link to={`/admin/tickets/${ticket.ticket_id}`}>
                        <Button size="sm" variant="ghost" className="text-teal-600 hover:text-teal-700 hover:bg-teal-50">
                          Ver
                        </Button>
                      </Link>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Paginación */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <p className="text-sm text-slate-500">
                Página {page + 1} de {totalPages} ({total.toLocaleString('es-ES')} total)
              </p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => loadTickets(page - 1)} disabled={page === 0}>
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="sm" onClick={() => loadTickets(page + 1)} disabled={page >= totalPages - 1}>
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}
      {/* Modal Hoja de Ruta IA */}
      <Dialog open={showRouteSheet} onOpenChange={setShowRouteSheet}>
        <DialogContent className="max-w-5xl h-[92vh] flex flex-col p-0 gap-0">

          {/* Header fijo con botones siempre visibles */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 flex-shrink-0 bg-white rounded-t-lg">
            <div className="flex items-center gap-2">
              <Navigation className="w-5 h-5 text-teal-600" />
              <span className="font-bold text-slate-900 text-base">Hoja de Ruta IA</span>
              {routeResult && (
                <span className="ml-2 bg-teal-100 text-teal-700 text-xs font-semibold px-2.5 py-0.5 rounded-full">
                  {routeResult.tickets_ordenados?.length || 0} paradas
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={downloadRoutePDF}
                disabled={pdfLoading || !routeResult}
                className="bg-red-600 hover:bg-red-700 text-white"
                size="sm"
                data-testid="btn-download-pdf-route"
              >
                {pdfLoading ? (
                  <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Generando...</>
                ) : (
                  <><FileText className="w-4 h-4 mr-1.5" /> Descargar PDF</>
                )}
              </Button>
              <Button onClick={() => window.print()} variant="outline" size="sm" data-testid="btn-print-route">
                <Printer className="w-4 h-4 mr-1.5" /> Imprimir
              </Button>
              <Button variant="outline" size="sm" onClick={() => setShowRouteSheet(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Contenido scrollable */}
          <div className="flex-1 overflow-y-auto px-6 py-4">
          {routeResult && (
            <div id="route-print-area">
              <style>{`
                @media print {
                  body > * { display: none !important; }
                  #route-print-area { display: block !important; position: fixed; top: 0; left: 0; width: 100%; }
                  .no-print { display: none !important; }
                }
              `}</style>

              {/* Cabecera */}
              <div className="flex items-start justify-between mb-4 pb-3 border-b border-slate-200">
                <div>
                  <h2 className="text-xl font-bold text-slate-900">Hoja de Ruta del Conductor</h2>
                  <p className="text-sm text-slate-500 capitalize">
                    {routeResult.fecha
                      ? new Date(routeResult.fecha + 'T12:00:00').toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
                      : ''}
                  </p>
                </div>
                <div className="text-right text-xs text-slate-400">
                  <p>Generado con IA · {new Date().toLocaleString('es-ES')}</p>
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-3 mb-4">
                <div className="bg-teal-50 border border-teal-200 rounded-xl p-3 text-center">
                  <p className="text-2xl font-bold text-teal-800">{routeResult.total_tickets}</p>
                  <p className="text-xs text-teal-600 font-medium">Recogidas</p>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-center">
                  <p className="text-2xl font-bold text-blue-800">
                    {routeResult.optimization?.tiempo_estimado_minutos != null
                      ? `${Math.floor(routeResult.optimization.tiempo_estimado_minutos / 60)}h ${routeResult.optimization.tiempo_estimado_minutos % 60}m`
                      : '—'}
                  </p>
                  <p className="text-xs text-blue-600 font-medium">Tiempo estimado</p>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 text-center">
                  <p className="text-2xl font-bold text-slate-800">
                    {routeResult.optimization?.distancia_estimada_km != null
                      ? `${Number(routeResult.optimization.distancia_estimada_km).toFixed(1)} km`
                      : '—'}
                  </p>
                  <p className="text-xs text-slate-600 font-medium">Distancia estimada</p>
                </div>
              </div>

              {/* Razonamiento IA */}
              {routeResult.optimization?.razonamiento && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4">
                  <p className="font-semibold text-amber-800 text-sm mb-1 flex items-center gap-1.5">
                    <Bot className="w-4 h-4" /> Criterio de optimización IA
                  </p>
                  <p className="text-xs text-amber-700 leading-relaxed">{routeResult.optimization.razonamiento}</p>
                </div>
              )}

              {/* Tabla de tickets ordenados */}
              <div className="overflow-x-auto rounded-xl border border-slate-200">
                <table className="w-full text-sm border-collapse" data-testid="route-sheet-table">
                  <thead>
                    <tr className="bg-slate-800 text-white">
                      <th className="px-3 py-2.5 text-center text-xs font-bold w-10">#</th>
                      <th className="px-3 py-2.5 text-left text-xs font-bold">Ciudadano</th>
                      <th className="px-3 py-2.5 text-left text-xs font-bold">Dirección</th>
                      <th className="px-3 py-2.5 text-left text-xs font-bold">Ruta</th>
                      <th className="px-3 py-2.5 text-left text-xs font-bold">Teléfono</th>
                      <th className="px-3 py-2.5 text-center text-xs font-bold">Items</th>
                      <th className="px-3 py-2.5 text-center text-xs font-bold">Estado</th>
                      <th className="px-3 py-2.5 text-center text-xs font-bold no-print">Conf.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(routeResult.tickets_ordenados || []).map((ticket, idx) => {
                      const numItems = ticket.num_items || (ticket.items?.length ?? 0);
                      return (
                        <tr key={ticket.ticket_id} className={`border-b border-slate-100 ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}`}>
                          <td className="px-3 py-2.5 text-center font-bold text-teal-600 text-base">{idx + 1}</td>
                          <td className="px-3 py-2.5">
                            <p className="font-semibold text-slate-800">{ticket.nombre_ciudadano}</p>
                            <p className="text-xs text-slate-400 font-mono">{ticket.ticket_id}</p>
                          </td>
                          <td className="px-3 py-2.5 text-slate-600 text-xs">
                            <p>{ticket.direccion || '—'}</p>
                            {(ticket.bloque || ticket.piso) && (
                              <p className="text-slate-400 mt-0.5">
                                {ticket.bloque && <span className="mr-1.5">Bl. {ticket.bloque}</span>}
                                {ticket.piso && <span>Piso {ticket.piso}</span>}
                              </p>
                            )}
                          </td>
                          <td className="px-3 py-2.5">
                            {ticket.lugar === 'Casco Antiguo' ? (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 border border-emerald-200 whitespace-nowrap">Casco Antiguo</span>
                            ) : ticket.lugar === 'Puerta Tierra' ? (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 border border-blue-200 whitespace-nowrap">Puerta Tierra</span>
                            ) : (
                              <span className="text-xs text-slate-400">{ticket.lugar || ticket.zona || '—'}</span>
                            )}
                          </td>
                          <td className="px-3 py-2.5 text-slate-500 font-mono text-xs">{ticket.telefono || '—'}</td>
                          <td className="px-3 py-2.5 text-center">
                            {numItems > 1 ? (
                              <span className="inline-flex items-center gap-1 bg-amber-100 text-amber-700 border border-amber-300 rounded-full px-2 py-0.5 text-xs font-bold">
                                ⚠ {numItems}
                              </span>
                            ) : (
                              <span className="font-bold text-slate-700">{numItems || '—'}</span>
                            )}
                          </td>
                          <td className="px-3 py-2.5 text-center">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border ${ESTADO_STYLES[ticket.estado] || 'bg-slate-100 text-slate-600 border-slate-200'}`}>
                              {ticket.estado}
                            </span>
                          </td>
                          <td className="px-3 py-2.5 text-center no-print">
                            <input type="checkbox" className="w-4 h-4 accent-teal-600" title="Confirmar recogida" />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <p className="text-xs text-slate-400 mt-3 text-center no-print">
                <MapPin className="w-3 h-3 inline mr-1" />
                Secuencia optimizada por IA · {routeResult.tickets_ordenados?.length || 0} paradas
              </p>
            </div>
          )}
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Modal Auto-Zonas (con pestañas) ─────────────────────────────────── */}
      <Dialog open={showRezonaModal} onOpenChange={(open) => {
        setShowRezonaModal(open);
        if (!open) { setRezonaJob(null); setGeocodeJob(null); }
      }}>
        <DialogContent className="max-w-lg" data-testid="rezona-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-teal-600" />
              Gestión de Zonas Automática
            </DialogTitle>
          </DialogHeader>

          {/* Pestañas */}
          <div className="flex border-b border-slate-200 mb-4">
            <button
              onClick={() => setRezonaTab('autozona')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${rezonaTab === 'autozona' ? 'border-teal-600 text-teal-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
              data-testid="tab-autozona"
            >
              Auto-Zonas por CP
            </button>
            <button
              onClick={() => setRezonaTab('geocodificar')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${rezonaTab === 'geocodificar' ? 'border-teal-600 text-teal-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
              data-testid="tab-geocodificar"
            >
              Geocodificar históricos
              <span className="ml-1.5 text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full">30.741</span>
            </button>
          </div>

          {/* ─── PESTAÑA 1: Auto-Zonas por CP ─────────────────────────────── */}
          {rezonaTab === 'autozona' && (
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                Analiza el <strong>código postal</strong> en la dirección y las <strong>coordenadas</strong> de cada ticket
                para asignar automáticamente <em>Casco Antiguo</em> o <em>Puerta Tierra</em>. Instantáneo.
              </p>

              {!rezonaJob && (
                <div className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-lg px-4 py-3">
                  <input
                    id="chk-solo-sin-zona"
                    type="checkbox"
                    checked={rezonaSoloSinZona}
                    onChange={e => setRezonaSoloSinZona(e.target.checked)}
                    className="w-4 h-4 accent-teal-600"
                    data-testid="chk-solo-sin-zona"
                  />
                  <label htmlFor="chk-solo-sin-zona" className="text-sm text-slate-700 cursor-pointer">
                    Solo tickets <strong>sin zona asignada</strong>
                    <span className="block text-xs text-slate-400 mt-0.5">
                      Desmarca para re-procesar también los que ya tienen zona
                    </span>
                  </label>
                </div>
              )}

              {rezonaJob && (
                <div className="space-y-3">
                  <div className="w-full bg-slate-200 rounded-full h-2">
                    <div className="bg-teal-500 h-2 rounded-full transition-all duration-300" style={{ width: `${rezonaJob.percent || 0}%` }} />
                  </div>
                  <p className="text-sm text-slate-600">{rezonaJob.message}</p>
                  <div className="grid grid-cols-2 gap-2 text-center">
                    <div className="bg-slate-50 rounded-lg p-2 border border-slate-200">
                      <p className="text-lg font-bold text-slate-800">{(rezonaJob.procesados || 0).toLocaleString('es-ES')}</p>
                      <p className="text-xs text-slate-500">Procesados</p>
                    </div>
                    <div className="bg-teal-50 rounded-lg p-2 border border-teal-200">
                      <p className="text-lg font-bold text-teal-700">{(rezonaJob.actualizados || 0).toLocaleString('es-ES')}</p>
                      <p className="text-xs text-teal-600">{rezonaJob.dry_run ? 'Se actualizarían' : 'Actualizados'}</p>
                    </div>
                    <div className="bg-amber-50 rounded-lg p-2 border border-amber-200">
                      <p className="text-lg font-bold text-amber-700">{(rezonaJob.sin_clasificar || 0).toLocaleString('es-ES')}</p>
                      <p className="text-xs text-amber-600">Sin clasificar</p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-2 border border-slate-200">
                      <p className="text-lg font-bold text-slate-600">{(rezonaJob.omitidos || 0).toLocaleString('es-ES')}</p>
                      <p className="text-xs text-slate-500">Ya correctos</p>
                    </div>
                  </div>
                  {rezonaJob.status === 'done' && (
                    <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-2 text-sm text-emerald-800 font-medium text-center">
                      {rezonaJob.dry_run ? `Simulación: se actualizarían ${(rezonaJob.actualizados||0).toLocaleString('es-ES')} tickets.` : `Completado. ${(rezonaJob.actualizados||0).toLocaleString('es-ES')} tickets actualizados.`}
                    </div>
                  )}
                  {rezonaJob.status === 'error' && (
                    <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-2 text-sm text-red-700">{rezonaJob.message}</div>
                  )}
                </div>
              )}

              <div className="flex gap-2 pt-1">
                {!rezonaJob || ['done','error'].includes(rezonaJob.status) ? (
                  <>
                    <Button onClick={() => startReasignarZonas(true)} variant="outline" size="sm" disabled={rezonaLoading} data-testid="btn-rezona-simular" className="flex-1">
                      {rezonaLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null} Simular
                    </Button>
                    <Button onClick={() => startReasignarZonas(false)} disabled={rezonaLoading} size="sm" data-testid="btn-rezona-ejecutar" className="flex-1 bg-teal-600 hover:bg-teal-700 text-white">
                      {rezonaLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <MapPin className="w-4 h-4 mr-1" />} Asignar ahora
                    </Button>
                  </>
                ) : (
                  <div className="flex items-center gap-2 text-sm text-slate-500">
                    <Loader2 className="w-4 h-4 animate-spin text-teal-500" /> Procesando... {rezonaJob.percent || 0}%
                  </div>
                )}
                <Button variant="ghost" size="sm" onClick={() => setShowRezonaModal(false)}>Cerrar</Button>
              </div>
            </div>
          )}

          {/* ─── PESTAÑA 2: Geocodificar históricos ──────────────────────── */}
          {rezonaTab === 'geocodificar' && (
            <div className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800">
                <strong>30.741 tickets históricos</strong> solo tienen nombre de calle sin CP.
                Geocodifica sus direcciones con ArcGIS para determinar la zona.
                El proceso puede ejecutarse en segundo plano — <strong>se puede detener y reanudar.</strong>
              </div>

              {/* Selector de velocidad */}
              {!geocodeJob && (
                <div className="space-y-2">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Velocidad</p>
                  <div className="grid grid-cols-3 gap-2">
                    {VELOCIDADES.map(v => (
                      <button
                        key={v.ms}
                        onClick={() => setGeocodeVelocidad(v.ms)}
                        className={`rounded-lg p-2.5 border text-center transition-colors ${geocodeVelocidad === v.ms ? 'border-teal-500 bg-teal-50 text-teal-800' : 'border-slate-200 hover:border-slate-300 text-slate-600'}`}
                        data-testid={`vel-${v.label.toLowerCase()}`}
                      >
                        <p className="text-sm font-semibold">{v.label}</p>
                        <p className="text-xs text-slate-400 mt-0.5">{v.desc}</p>
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-slate-400">
                    La velocidad "Rápida" puede acercarse a los límites de la API de ArcGIS.
                    Se recomienda "Normal" para un uso nocturno estable.
                  </p>
                </div>
              )}

              {/* Progreso del job de geocodificación */}
              {geocodeJob && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between text-xs text-slate-500">
                    <span>{geocodeJob.percent || 0}%</span>
                    {geocodeJob.eta_minutos != null && geocodeJob.status === 'running' && (
                      <span>ETA: ~{geocodeJob.eta_minutos < 60 ? `${geocodeJob.eta_minutos} min` : `${(geocodeJob.eta_minutos/60).toFixed(1)} h`}</span>
                    )}
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all duration-500 ${geocodeJob.status === 'stopped' ? 'bg-amber-400' : 'bg-teal-500'}`}
                      style={{ width: `${geocodeJob.percent || 0}%` }}
                    />
                  </div>
                  <p className="text-sm text-slate-600">{geocodeJob.message}</p>

                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div className="bg-slate-50 rounded-lg p-2 border border-slate-200">
                      <p className="text-base font-bold text-slate-800">{(geocodeJob.procesados || 0).toLocaleString('es-ES')}</p>
                      <p className="text-xs text-slate-500">Procesados</p>
                    </div>
                    <div className="bg-teal-50 rounded-lg p-2 border border-teal-200">
                      <p className="text-base font-bold text-teal-700">{(geocodeJob.clasificados || 0).toLocaleString('es-ES')}</p>
                      <p className="text-xs text-teal-600">Clasificados</p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-2 border border-slate-200">
                      <p className="text-base font-bold text-slate-500">{(geocodeJob.fallidos || 0).toLocaleString('es-ES')}</p>
                      <p className="text-xs text-slate-400">Sin resultado</p>
                    </div>
                  </div>

                  {geocodeJob.status === 'done' && (
                    <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-2 text-sm text-emerald-800 font-medium text-center">
                      Completado. {(geocodeJob.clasificados||0).toLocaleString('es-ES')} tickets reclasificados.
                    </div>
                  )}
                  {geocodeJob.status === 'stopped' && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-2 text-sm text-amber-800 text-center">
                      Detenido. Puedes retomar — solo procesará los que aún no tienen zona.
                    </div>
                  )}
                  {geocodeJob.status === 'error' && (
                    <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-2 text-sm text-red-700">{geocodeJob.message}</div>
                  )}
                </div>
              )}

              <div className="flex gap-2 pt-1">
                {!geocodeJob || ['done','error','stopped'].includes(geocodeJob.status) ? (
                  <Button
                    onClick={startGeocoding}
                    disabled={geocodeLoading}
                    size="sm"
                    data-testid="btn-geocodificar-iniciar"
                    className="flex-1 bg-teal-600 hover:bg-teal-700 text-white"
                  >
                    {geocodeLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <MapPin className="w-4 h-4 mr-1" />}
                    {geocodeJob?.status === 'stopped' ? 'Reanudar' : 'Iniciar geocodificación'}
                  </Button>
                ) : (
                  <>
                    <div className="flex items-center gap-2 text-sm text-slate-500 flex-1">
                      <Loader2 className="w-4 h-4 animate-spin text-teal-500" />
                      Geocodificando... {geocodeJob.percent || 0}%
                    </div>
                    <Button
                      onClick={stopGeocoding}
                      variant="outline"
                      size="sm"
                      data-testid="btn-geocodificar-detener"
                      className="border-red-300 text-red-600 hover:bg-red-50"
                    >
                      Detener
                    </Button>
                  </>
                )}
                <Button variant="ghost" size="sm" onClick={() => setShowRezonaModal(false)}>Cerrar</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminTickets;
